import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChartLine,
  faCheckCircle,
  faCog,
  faExclamationCircle,
  faGlobe,
} from '@fortawesome/pro-light-svg-icons'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import {
  Box, Button, Columns, DashboardModule, LoadingModule, Tools,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import useCurrentUser from '@hooks/useCurrentUser'
import useIntegrationData from '@hooks/useIntegrationData'
import useInvoice, { generateUrls } from '@hooks/useInvoice'
import useInvoiceIntegrationSettings from '@hooks/useInvoiceIntegrationSettings'
import useInvoices from '@hooks/useInvoices'

import AuthenticatedDownload from '@components/AuthenticatedDownload'
import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

import InvoicesTable from '../InvoicesTable'
import XeroIntegrationButton from '../XeroIntegrationButton'
import useExportInvoices from '../../hooks/useExportInvoices'

const buildPageFilterFields = (
  currentUserPayload: ReturnType<typeof useCurrentUser>,
) => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  const { provider } = currentUserPayload

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Invoice Number, Item ID or Reference',
    },
    {
      callbacks: {
        shouldRender: ({ debtor_type }: { debtor_type?: string }) => !debtor_type,
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
      },
      key: 'brand_id',
    },
    {
      callbacks: {
        shouldRender: ({ brand_id }: { brand_id?: number }) => !brand_id,
      },
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'debtor_type',
      label: 'Filter by Debtor Type (Entity ID required)',
      values: [
        { key: 'Agency', label: 'Agency' },
        { key: 'Brand', label: 'Brand' },
        { key: 'Provider', label: 'Provider' },
      ],
    },
    {
      callbacks: {
        shouldRender: ({ debtor_type }: { debtor_type?: string }) => debtor_type === 'Agency',
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'agencies',
      },
      dependentKey: 'debtor_type',
      key: 'debtor_agency_id',
    },
    {
      callbacks: {
        shouldRender: ({ debtor_type }: { debtor_type?: string }) => debtor_type === 'Brand',
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
      },
      dependentKey: 'debtor_type',
      key: 'debtor_brand_id',
    },
    {
      callbacks: {
        shouldRender: ({ debtor_type }: { debtor_type?: string }) => debtor_type === 'Provider',
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
        selectedItemId: provider?.id,
      },
      dependentKey: 'debtor_type',
      key: 'debtor_provider_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'start_date',
      label: 'Created After',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'end_date',
      label: 'Created Before',
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

type QueryParams = { approved?: 'true' | 'false' }

const getFilterType = (approvedParam: QueryParams['approved']) => {
  if (approvedParam === 'true') return 'approved'
  if (approvedParam === 'false') return 'unapproved'
  return 'all'
}

export type UseIntegrationDataType = ReturnType<typeof useIntegrationData>

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props
  const { debtor_agency_id, debtor_brand_id, debtor_provider_id } = pageFilters

  useLocation()

  const { approved }: QueryParams = parsePermittedQueryParams(
    getQueryParamsFromHash(),
    ['approved'],
  )

  const invoicesFilters = {
    ...pageFilters,
    approved,
    debtor_id: debtor_agency_id || debtor_brand_id || debtor_provider_id,
    void: 'false' as const,
  }

  const invoicesPayload = useInvoices({
    filters: invoicesFilters,
    performHttpRequests: true,
    requestOptions: {
      // @ts-expect-error TS2353
      include_invoice_approved_order_item_count: true,
      include_invoice_debtor: true,
    },
  })

  const initInvoice = {
    creditor_type: 'Provider',
  }

  const useInvoicePayload = useInvoice(initInvoice)
  const {
    callbacks: { createAdminInvoice },
  } = useInvoicePayload

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredInvoices,
    filteredInvoicesCount,
    hasFilteredInvoices,
    loading,
  } = invoicesPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: { resetFilters, updateFilters },
    defaultFilters,
  } = pageContext

  // @ts-expect-error TS2554
  const { invoicesIndexUrl, approvedInvoicesUrl, unapprovedInvoicesUrl } = generateUrls()

  const filterType = getFilterType(approved)

  const currentUserPayload = useCurrentUser()
  const { currentUser, isProviderDirector } = currentUserPayload

  // At this stage, only provider directors can perform the request.
  // Will have to update the condition in the future
  const integrationDataPayload = useIntegrationData({
    performHttpRequests: isProviderDirector,
    requestOptions: { service_provider: 'xero' },
  })

  const { hasActiveSession, integrationData } = integrationDataPayload

  const invoiceIntegrationSettingsPayload = useInvoiceIntegrationSettings({
    integrationData,
    performHttpRequests: isProviderDirector,
    requestOptions: { service_provider: 'xero' },
  })

  const {
    callbacks: {
      invoiceIntegrationSettings,
    },
    loading: loadingAccountingProvider,
  } = invoiceIntegrationSettingsPayload

  const { exportInvoicesCsvUrl, loadingCsv, setLoadingCsv } = useExportInvoices(invoicesFilters)

  return (
    <>
      <Helmet>
        <title>Invoices | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey={filterType}
        actionContent={(
          <>
            <ScopedConditionalContent
              permittedRoleTitles={['Provider Director']}
            >
              <XeroIntegrationButton
                // @ts-expect-error TS2322
                currentUserId={currentUser?.id}
                integrationDataPayload={integrationDataPayload}
              />
            </ScopedConditionalContent>

            <ScopedConditionalContent
              permittedRoleTitles={['Admin', 'Support', 'Provider Director']}
            >
              <Button
                buttonStyle="primaryCreate"
                onClick={() => createAdminInvoice()}
                size="medium"
                width="auto"
              >
                Create
              </Button>
            </ScopedConditionalContent>
          </>
        )}
        tabBarItems={[
          {
            href: invoicesIndexUrl,
            icon: faGlobe,
            key: 'all',
            title: 'All Invoices',
          },
          {
            href: approvedInvoicesUrl,
            icon: faExclamationCircle,
            key: 'approved',
            title: 'Approved',
          },
          {
            href: unapprovedInvoicesUrl,
            icon: faCheckCircle,
            key: 'unapproved',
            title: 'Unapproved',
          },
        ]}
        title="Invoices"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns
          boxProps={{ marginTop: 'large' }}
          flexDirection={['column', 'column', 'row']}
        >
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredInvoices && <ListBlankState />}

            {!loading && hasFilteredInvoices && (
              <DashboardModule title="Invoices">
                <Box flexDirection="column">
                  <InvoicesTable invoices={filteredInvoices} />

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredInvoicesCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            {hasActiveSession && !loadingAccountingProvider && (
              <ScopedConditionalContent
                permittedRoleTitles={['Provider Director']}
              >
                <Tools>
                  <Button
                    buttonStyle="secondaryUtility"
                    icon={<FontAwesomeIcon icon={faCog} />}
                    onClick={() => invoiceIntegrationSettings()}
                    size="medium"
                  >
                    Integration Settings
                  </Button>
                </Tools>
              </ScopedConditionalContent>
            )}

            <ScopedConditionalContent
              permittedRoleTitles={['Admin', 'Support']}
            >
              <Tools>
                <AuthenticatedDownload
                  callbacks={{
                    afterAction: () => setLoadingCsv(false),
                    beforeAction: () => setLoadingCsv(true),
                  }}
                  key="download-csv"
                >
                  <Button
                    buttonStyle="secondaryUtility"
                    // @ts-expect-error TS2322
                    href={exportInvoicesCsvUrl}
                    icon={<FontAwesomeIcon icon={faChartLine} />}
                    key="download"
                    loading={loadingCsv}
                    size="medium"
                  >
                    Download as CSV
                  </Button>
                </AuthenticatedDownload>
              </Tools>
            </ScopedConditionalContent>

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(currentUserPayload)}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
